import { useEffect, useRef, useState, type FormEvent } from 'react';
import { CoroaVip } from './CoroaVip';
import { SeloFundador } from './SeloFundador';
import { ehGesto } from '../world/balao';
import { hora, t } from '../i18n/idioma';
import { nomeDaSkill } from '../i18n/nomes';
import type { ChatLine, JournalLine, JournalTone } from '../world/types';
import { IcBalao, IcBencao, IcDano, IcEtiqueta, IcGlobo, IcMaisGente, IcPergaminho } from './icones/IconesV3';
import type { AbaChat } from './hud/useChatNaoLidas';

function textoDaLinha(line: JournalLine): string {
  if (!line.chave) return line.text;
  if (!line.args) return t(line.chave);
  const args = { ...line.args };
  if (typeof args.skill === 'string') args.skill = nomeDaSkill(args.skill);
  if (typeof args.atributo === 'string') args.atributo = t(`atributo.${args.atributo}`);
  return t(line.chave, args);
}

/** `hh:mm Nick: mensagem` — horário, nick e texto em peças distintas. */
export function ChatLineView({ line }: { line: ChatLine }) {
  return (
    <p className="journal__say">
      <time className="journal__t">{hora(line.at)}</time>
      {' '}
      <span className="journal__nick">
        {line.selo ? <SeloFundador nivel={line.selo} className="journal__selo" /> : null}
        {line.name}
        {line.vip ? <CoroaVip className="journal__vip" /> : null}
      </span>
      <span className="journal__colon">:</span>
      {' '}
      {/* Fala de jogador é do jogador: nunca passa pelo resource. */}
      <span className={ehGesto(line.text) ? 'journal__msg journal__emote' : 'journal__msg'}>{line.text}</span>
    </p>
  );
}

export function LinhaDiario({ line }: { line: JournalLine }) {
  return (
    <p className={line.tone}>
      <time className="journal__t">{hora(line.at)}</time>
      {' '}
      <span className="journal__msg">{textoDaLinha(line)}</span>
    </p>
  );
}

type Tab = AbaChat;

/** Uma conversa privada com um amigo, aberta numa aba do chat (techspec-hud-v3 §23). */
export interface ConversaChat {
  key: string;
  nome: string;
  online: boolean;
  naoLidas: number;
  linhas: readonly ChatLine[];
}

interface ChatGumpProps {
  journal: readonly JournalLine[];
  local?: readonly ChatLine[];
  combat?: readonly JournalLine[];
  global: readonly ChatLine[];
  trade: readonly ChatLine[];
  guild?: readonly ChatLine[];
  alliance?: readonly ChatLine[];
  hasGuild?: boolean;
  hasAlliance?: boolean;
  net: boolean;
  onSay?: (via: 0 | 1 | 2 | 4 | 5, text: string) => void;
  /** Muda para focar o campo (Enter no mundo). Depois de enviar, o foco volta ao jogo. */
  focoSeq?: number;
  aba: Tab;
  onAba(aba: Tab): void;
  naoLidas: Readonly<Record<string, number>>;
  variante: 'janela' | 'folha';
  onMinimizar?: () => void;
  /** As abas de conversa com amigos, depois dos canais; `conversa` é a aberta (tem a frente sobre `aba`). */
  conversas?: readonly ConversaChat[];
  conversa?: string | null;
  onConversa?(key: string): void;
  onFecharConversa?(key: string): void;
  onTell?(nome: string, texto: string): void;
  tellNotice?: string | null;
}

export const TABS: ReadonlyArray<{ id: Tab; chave: string }> = [
  { id: 'diario', chave: 'diario.titulo' },
  { id: 'local', chave: 'diario.local' },
  { id: 'combate', chave: 'diario.combate' },
  { id: 'global', chave: 'diario.globo' },
  { id: 'comercio', chave: 'diario.comercio' },
  { id: 'guilda', chave: 'diario.guilda' },
  { id: 'alianca', chave: 'diario.alianca' }
];

const MAX_DIARIO = 200;
let focoAtendido = 0;
/**
 * Limpar (Renato, 28/10/2026): o corte de cada aba ou conversa. Diário e Combate cortam pelo id da linha; os canais
 * e as conversas, pelo horário da última fala. Só esconde aqui; o histórico continua com o limite de sempre. Fica no
 * módulo para sobreviver ao minimizar (o chat desmonta).
 */
const cortes = new Map<string, number>();
/** Distância do fim (px) em que o chat ainda acompanha as falas novas. Mais acima, o jogador está lendo o histórico. */
const GRUDA_NO_FIM = 32;
const MAX = 80;
const READONLY: ReadonlySet<Tab> = new Set(['diario', 'combate']);

export function ChatGump({
  journal, local = [], combat = [], global, trade, guild = [], alliance = [], hasGuild = false, hasAlliance = false, net, onSay,
  focoSeq = 0, aba, onAba, naoLidas, variante, onMinimizar,
  conversas = [], conversa = null, onConversa, onFecharConversa, onTell, tellNotice = null
}: ChatGumpProps) {
  const conv = conversas.find(c => c.key === conversa) ?? null;
  const ref = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const peloAtalho = useRef(false);
  const [draft, setDraft] = useState('');
  const [, setLimpezas] = useState(0);
  /** O log estava no fim antes da última atualização? Só então a fala nova puxa a rolagem para baixo. */
  const noFim = useRef(true);

  useEffect(() => {
    // O chat desmonta quando minimiza; o pedido de foco vale uma vez só.
    if (focoSeq <= focoAtendido) return;
    focoAtendido = focoSeq;
    peloAtalho.current = true;
    campo.current?.focus();
  }, [focoSeq]);

  const visiveis = TABS.filter(item =>
    item.id === 'diario' || item.id === 'local' || item.id === 'global' || item.id === 'comercio' || item.id === 'combate'
    || (item.id === 'guilda' && hasGuild)
    || (item.id === 'alianca' && hasAlliance));

  useEffect(() => {
    if (conv) return;
    if (aba === 'guilda' && !hasGuild) onAba('diario');
    if (aba === 'alianca' && !hasAlliance) onAba('diario');
  }, [aba, hasGuild, hasAlliance, onAba, conv]);

  // Trocar de aba ou de conversa: vai para o fim.
  useEffect(() => {
    noFim.current = true;
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [aba, conv?.key]);

  // Fala nova: acompanha só se o jogador estava no fim. Antes toda linha nova (de qualquer canal, até do diário com a
  // aba Local aberta) puxava para baixo, e não dava para subir a rolagem no meio do combate.
  useEffect(() => {
    const el = ref.current;
    if (el && noFim.current) el.scrollTop = el.scrollHeight;
  }, [journal, local, combat, global, trade, guild, alliance, conv?.linhas, tellNotice]);

  const aoRolar = () => {
    const el = ref.current;
    if (!el) return;
    noFim.current = el.scrollHeight - el.scrollTop - el.clientHeight <= GRUDA_NO_FIM;
  };

  const viaOf = (id: Tab): 0 | 1 | 2 | 4 | 5 | null => {
    if (id === 'local') return 0;
    if (id === 'comercio') return 2;
    if (id === 'guilda') return 4;
    if (id === 'alianca') return 5;
    if (id === 'global') return 1;
    return null;
  };

  // Enter no campo vazio, ou depois de enviar quando o campo veio do atalho: o foco volta ao
  // mundo, e WASD anda de novo.
  const soltar = () => {
    peloAtalho.current = false;
    campo.current?.blur();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) { soltar(); return; }
    if (conv) {
      // Offline pela lista (que atualiza a cada minuto) ainda tenta: se não estiver no mundo, o servidor avisa.
      if (!onTell) return;
      onTell(conv.nome, text);
      setDraft('');
      if (peloAtalho.current) soltar();
      return;
    }
    const via = viaOf(aba);
    if (!onSay || via == null) return;
    onSay(via, text);
    setDraft('');
    if (peloAtalho.current) soltar();
  };

  // A aba Local fala mesmo sem servidor (o balão aparece só aqui); as outras precisam da rede.
  const podeFalar = conv ? !!onTell : !READONLY.has(aba) && onSay && (net || aba === 'local');
  const chaveCorte = conv ? `tell:${conv.key}` : aba;
  const corte = cortes.get(chaveCorte) ?? -Infinity;
  const shownLog = (aba === 'combate' ? combat : journal).slice(-(aba === 'diario' ? MAX_DIARIO : MAX))
    .filter(l => l.id > corte);
  const shownChat = (
    aba === 'local' ? local
    : aba === 'comercio' ? trade
      : aba === 'guilda' ? guild
        : aba === 'alianca' ? alliance
          : global
  ).slice(-MAX).filter(l => l.at > corte);
  const linhasConv = conv ? conv.linhas.filter(l => l.at > corte) : [];

  const limpar = () => {
    const ultimo = conv ? linhasConv[linhasConv.length - 1]?.at
      : (aba === 'diario' || aba === 'combate') ? shownLog[shownLog.length - 1]?.id
        : shownChat[shownChat.length - 1]?.at;
    if (ultimo == null) return;
    cortes.set(chaveCorte, ultimo);
    noFim.current = true;
    setLimpezas(n => n + 1);
  };
  const temLinhas = conv ? linhasConv.length > 0
    : (aba === 'diario' || aba === 'combate') ? shownLog.length > 0 : shownChat.length > 0;

  const label = conv ? t('tell.mensagemPara', { nome: conv.nome })
    : aba === 'diario' ? t('diario.titulo')
    : aba === 'local' ? t('diario.chatLocal')
    : aba === 'global' ? t('diario.chatGlobal')
      : aba === 'comercio' ? t('diario.chatComercio')
        : aba === 'combate' ? t('diario.chatCombate')
          : aba === 'guilda' ? t('diario.chatGuilda')
            : t('diario.chatAlianca');

  const placeholder = conv ? (conv.online ? t('tell.digaAlgo') : t('hud.chat.tellOffline', { nome: conv.nome }))
    : aba === 'local' ? t('diario.digaAQuemEstaPerto')
    : aba === 'comercio' ? t('diario.anuncieCompraOuVenda')
    : aba === 'guilda' ? t('diario.faleComACasa')
      : aba === 'alianca' ? t('diario.faleComAAlianca')
        : t('diario.digaAlgo');

  const logTab = !conv && (aba === 'diario' || aba === 'combate');
  const cor = conv ? COR_TELL : COR[aba] ?? 'var(--gold-lit)';
  const Ic = conv ? IcBalao : ICONE[aba];
  const canal = conv ? conv.nome : t(TABS.find(x => x.id === aba)!.chave);
  const soLeitura = conv ? t('hud.amg.estaOffline', { nome: conv.nome })
    : logTab ? t(aba === 'combate' ? 'hud.chat.soLeituraCombate' : 'hud.chat.soLeituraDiario') : t('hud.chat.semRede');

  const fala = (line: ChatLine, i: number) => (
    <p key={`${line.at}-${line.name}-${i}`} className="ct-l ct-l--fala" style={{ ['--ct-cor' as string]: cor }}>
      <time>{hora(line.at)}</time>
      <b>{line.selo ? <SeloFundador nivel={line.selo} className="ct-selo" /> : null}{line.name}{line.vip ? <CoroaVip className="ct-vip" /> : null}</b>:{' '}
      {/* Fala de jogador é do jogador: nunca passa pelo resource. */}
      {ehGesto(line.text) ? <em>{line.text}</em> : <span className="journal__msg">{line.text}</span>}
    </p>
  );

  /* HUD V3 (techspec-hud-v3 §12): canais em ícone, o nome só no aberto, não lidas no ícone; linhas
     menores com o ponto do tom; o nick na cor do canal; o horário só no hover; a entrada em pílula. */
  return (
    <>
      <div className="ct-topo">
        <div className="ct-abas" role="tablist" aria-label={t('diario.tituloLongo')}>
          {visiveis.map(i => {
            const C = ICONE[i.id];
            const n = i.id !== 'diario' ? naoLidas[i.id] ?? 0 : 0;
            const nome = t(i.chave);
            return (
              <button key={i.id} type="button" role="tab" className="ct-aba" aria-selected={!conv && aba === i.id} aria-pressed={!conv && aba === i.id}
                title={nome} aria-label={n ? `${nome} · ${n}` : nome} onClick={() => onAba(i.id)}>
                <C /><span>{nome}</span>{n > 0 && <i>{n}</i>}
              </button>
            );
          })}
          {/* §23: uma aba por amigo, com o nome dele e o ✕ que fecha a aba (a conversa continua guardada). */}
          {conversas.map(c => (
            <span key={c.key} className={`ct-conv${conv?.key === c.key ? ' is-on' : ''}`}>
              <button type="button" role="tab" className="ct-aba ct-aba--tell" aria-selected={conv?.key === c.key} aria-pressed={conv?.key === c.key}
                title={c.online ? t('tell.mensagemPara', { nome: c.nome }) : `${c.nome} · ${t('hud.amg.offlineSemMsg')}`}
                aria-label={c.naoLidas ? `${t('tell.mensagemPara', { nome: c.nome })} · ${c.naoLidas}` : t('tell.mensagemPara', { nome: c.nome })}
                onClick={() => onConversa?.(c.key)}>
                <s className={c.online ? 'is-on' : undefined} aria-hidden="true" /><span>{c.nome}</span>{c.naoLidas > 0 && <i>{c.naoLidas}</i>}
              </button>
              <button type="button" className="ct-conv__x" aria-label={t('hud.chat.fecharConversa', { nome: c.nome })}
                title={t('hud.chat.fecharConversa', { nome: c.nome })} onClick={() => onFecharConversa?.(c.key)}>✕</button>
            </span>
          ))}
        </div>
        <button type="button" className="ct-limpar" aria-label={t('hud.chat.limpar')} title={t('hud.chat.limpar')}
          disabled={!temLinhas} onClick={limpar}>
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 4.5h10M6.2 4.5V3h3.6v1.5M4.5 4.5l.7 8.5h5.6l.7-8.5M7 6.8v4.4M9 6.8v4.4" /></svg>
        </button>
        {variante === 'janela' && (
          <button type="button" className="ct-min" aria-label={t('hud.chat.minimizar')} title={t('hud.chat.minimizar')} onClick={onMinimizar}><i aria-hidden="true" /></button>
        )}
      </div>
      <div className="ct-log journal" ref={ref} onScroll={aoRolar} role="log" aria-live="polite" aria-label={label} data-nodrag>
        {conv
          ? <>
            {linhasConv.length === 0 && <p className="ct-l ct-l--note"><span className="journal__msg">{t('hud.amg.semMensagem')}</span></p>}
            {linhasConv.map(fala)}
            {tellNotice && <p className="ct-l ct-l--warn"><span className="journal__msg">{tellNotice}</span></p>}
          </>
          : logTab
          ? shownLog.map(line => (
            <p key={line.id} className={`ct-l ct-l--${TOM[line.tone] ?? 'plain'}`}>
              <time>{hora(line.at)}</time><span className="journal__msg">{textoDaLinha(line)}</span>
            </p>
          ))
          : shownChat.length === 0 && aba === 'local'
            ? <p className="ct-l ct-l--note"><span className="journal__msg">{t('diario.localVazio')}</span></p>
            : shownChat.map(fala)}
      </div>
      <form className="ct-dizer" onSubmit={submit} data-nodrag style={{ ['--ct-cor' as string]: cor }}>
        {podeFalar ? (
          <>
            <label className="ct-pilula">
              {/* Parado, o chat fica transparente e a pílula só lembra a tecla (v3-chat.css). */}
              {variante === 'janela' && <kbd className="ct-tecla" aria-hidden="true">Enter</kbd>}
              <span className="ct-canal">{canal}</span>
              <input
                ref={campo}
                type="text"
                maxLength={80}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={() => { peloAtalho.current = false; }}
                placeholder={placeholder}
                aria-label={label}
                autoComplete="off"
              />
            </label>
            {/* Sem o preventDefault, o clique tira o foco do campo antes de terminar; com o campo sem foco, o chat
                transparente esconde a seta (v3-chat.css) e o clique se perde: só o Enter enviava. */}
            <button type="submit" className="ct-enviar" aria-label={t('hud.chat.enviar')}
              onMouseDown={e => e.preventDefault()} onPointerDown={e => e.preventDefault()}>➤</button>
          </>
        ) : (
          <span className="ct-pilula is-so"><Ic />{soLeitura}</span>
        )}
      </form>
    </>
  );
}

/** A cor da conversa privada (techspec-hud-v3 §23). */
const COR_TELL = '#f0a6d8';
/** A cor de cada canal: o nick e o rótulo da pílula. */
const COR: Partial<Record<Tab, string>> = {
  local: '#e9dcae', combate: '#e07a6a', global: '#8fc3ff', comercio: '#e6c452', guilda: '#9fe3a0', alianca: '#c9a7ff'
};
const ICONE: Record<Tab, typeof IcPergaminho> = {
  diario: IcPergaminho, local: IcBalao, combate: IcDano, global: IcGlobo, comercio: IcEtiqueta, guilda: IcBencao, alianca: IcMaisGente
};
const TOM: Partial<Record<JournalTone, string>> = { system: 'sys', note: 'note', warn: 'warn', gain: 'gain', spell: 'golpe' };
